import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type FormEvent,
    type ReactNode,
} from "react";
import { ProgressBar } from "./ProgressBar";
import { Button } from "./Button";

// StepShell — the layout backbone for a single step in a multi-step flow.
// Full-height flex column:
//   PROGRESS LINE (top edge) · CONTENT (scrollable, gap-6) · FOOTER (Back | primary)
//
// `progressVariant` changes how that first line is drawn, and it is one
// decision with three halves rather than three props:
//   rail (default) — its own 6px row flush to the top edge, groove and all.
//     The line IS the top of the card.
//   bare — no groove, and OVERLAID on the header's top edge rather than given
//     a row of its own. For a flow whose header is a solid block (the therapist
//     wizard's dark brand strip): a grooved row above the block would put a
//     light band between the top of the screen and the block, and the block
//     would stop reading as the top edge. Drawn on the block, the fill is
//     coral on --rail-dark and the block stays flush to the top.
//
//     Overlaid, not stacked, because it must not occupy layout: the wizard's
//     splash renders the same header with NO progress line, and a 6px row
//     would drop the wordmark by 6px the moment the flow started. Absolute
//     positioning is what keeps the mark in one place across that handover.
//
//     With no `header` at all there is no block to overlay, so `bare` gives
//     the line its own row (still trackless). The alternative — drawing it
//     nowhere — makes the progress bar vanish because a different prop is
//     unset, which is the kind of coupling a caller cannot see.
//
// Gutter is px-6 md:px-8 (24/32px); content children are separated by gap-6.
// Navigation lives in the footer, in one of two layouts (`footerVariant`):
//   fullWidth (default) — outline "Back" on the left (when `showBack`) with
//     the primary action (passed via `footer`) filling the remaining width;
//     first / terminal screens omit Back and the primary goes full-width.
//   compact — hairline above the footer, natural-width outline "Back" on the
//     left, footer content right-aligned at its natural width (the hi-fi
//     wizard treatment: Back bottom-left, ~natural-width primary bottom-right).
//     Same outline treatment as fullWidth, without the flex-1 stretch, and at
//     `xl` (48px) because the flows using it put an xl primary opposite.
//
// Presentation-only — no flow/business logic. Canonical tokens only.

export interface StepShellProps {
    children: ReactNode;
    /** Persistent chrome above the content, below the progress line (e.g. a
     *  brand/help header). Outside the onSubmit form. Absent ⇒ unchanged: with
     *  `progressVariant="bare"` the line falls back to its own row rather than
     *  disappearing along with the block it would have been overlaid on. */
    header?: ReactNode;
    /** The primary action region (typically a full-width Button). */
    footer?: ReactNode;
    /**
     * The step's question, PINNED above the scroller instead of scrolling with the
     * answers.
     *
     * Passing a `StepHeading` as a child puts it inside the scroll area, which is
     * right for a short step and wrong for a long one: on the focus-areas step the
     * heading is 103px of a 718px scroller, so it both eats a seventh of the space
     * the options need AND slides away as you reach the last of them, leaving eight
     * checkboxes and no question. Pinned, it stays put and the options get the room.
     *
     * OPT-IN, not the default. A step whose content is shorter than the scroller
     * gains nothing, and moving every heading out at once would change the vertical
     * rhythm of screens nobody complained about.
     *
     * Distinct from `header`, which is persistent chrome ABOVE this and outside the
     * form (the wizard's brand strip). This sits inside the form, in the content
     * gutter, and belongs to the step.
     */
    heading?: ReactNode;
    showBack?: boolean;
    onBack?: () => void;
    /** Back-button copy. */
    backLabel?: ReactNode;
    /** Footer layout. `"fullWidth"` (default) keeps the legacy footer —
     *  outline Back (flex-1) + primary filling the rest. `"compact"` renders
     *  a hairline top border, a natural-width outline Back on the left, and the
     *  footer content right-aligned at its natural width. */
    footerVariant?: "fullWidth" | "compact";
    /** Where the progress line sits and how it is drawn. See the header note. */
    progressVariant?: "rail" | "bare";
    currentStep?: number;
    totalSteps?: number;
    progressPercent?: number;
    onSubmit?: (e: FormEvent) => void;
}

export function StepShell({
    children,
    header,
    heading,
    footer,
    showBack = false,
    onBack,
    backLabel = "Back",
    footerVariant = "fullWidth",
    progressVariant = "rail",
    currentStep,
    totalSteps,
    progressPercent,
    onSubmit,
}: StepShellProps) {
    const showProgress =
        (currentStep !== undefined && totalSteps !== undefined) ||
        progressPercent !== undefined;
    const showBackBtn = showBack && !!onBack;
    const compactFooter = footerVariant === "compact";
    const bareProgress = progressVariant === "bare";

    const progress = showProgress ? (
        <ProgressBar
            currentStep={currentStep}
            totalSteps={totalSteps}
            percent={progressPercent}
            track={!bareProgress}
        />
    ) : null;

    // Paint the page behind the card, so overscroll on mobile shows the card's
    // own ground rather than the browser's white.
    //
    // --surface, the card, replacing a literal --muted. On mobile the card is
    // full-bleed and IS the screen, so --muted put the recede colour ("hover
    // feedback, disabled fill, progress tracks") behind the whole flow and
    // showed as a grey band the moment anyone overscrolled a cream card.
    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "var(--surface)";
        return () => {
            document.body.style.backgroundColor = prev;
        };
    }, []);

    // The footer scroll-fade should only show when there's genuinely more
    // content below — otherwise it masks content sitting right above the footer.
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showFade, setShowFade] = useState(false);

    const measureFade = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const overflowing = el.scrollHeight > el.clientHeight + 1;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        setShowFade(overflowing && !atBottom);
    }, []);

    // Re-measure when the content changes (new step, async content, etc.).
    useLayoutEffect(() => {
        measureFade();
    }, [children, measureFade]);

    // And again once the webfonts have swapped. This is the measurement that
    // was wrong: the first one runs against fallback metrics, the serif lands a
    // few px taller, and the ResizeObserver below never hears about it because
    // the boxes it watches are sized by the flex column and do not change — only
    // the text inside them reflows. So a screen measured mid-swap kept a fade it
    // no longer needed for as long as it stayed open.
    //
    // `document.fonts` is guarded: jsdom has no FontFaceSet, and the test suites
    // that mount this shell should not need one.
    useEffect(() => {
        let live = true;
        document.fonts?.ready.then(() => {
            if (live) measureFade();
        });
        return () => {
            live = false;
        };
    }, [measureFade]);

    // Track scroll position + viewport resize + CONTENT resize.
    //
    // The children are observed as well as the scroller, and that is the whole
    // point rather than a precaution. The scroller's own box is pinned by the
    // flex column, so it does NOT resize when the content inside it grows or
    // shrinks — which is exactly when the answer changes. Observing only the
    // scroller meant the first measurement, taken before webfonts had swapped
    // and the entrance had settled, was the last one: a screen that briefly
    // overflowed by 3px kept the fade for as long as it was open, drawing a
    // gradient over content that had since fitted. Nothing re-rendered, so the
    // layout effect below never ran again either.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener("scroll", measureFade, { passive: true });
        const ro = new ResizeObserver(measureFade);
        ro.observe(el);
        for (const child of el.children) ro.observe(child);
        return () => {
            el.removeEventListener("scroll", measureFade);
            ro.disconnect();
        };
    }, [measureFade, children]);

    const inner = (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* The pinned heading TAKES OVER the scroller's top padding rather than
                adding to it, and carries the same `gap-6` (24px) underneath as the
                separation between children the scroller would have given it. So a
                step reads identically whether its heading is pinned or scrolled —
                only its behaviour changes, which is the whole point. */}
            {heading != null && (
                <div className="shrink-0 px-6 md:px-8 pt-6 md:pt-8 pb-6">{heading}</div>
            )}
            <div
                ref={scrollRef}
                className={`flex-1 min-h-0 overflow-y-auto px-6 md:px-8 flex flex-col gap-6 ${
                    heading != null ? "" : "pt-6 md:pt-8"
                }`}
            >
                {children}
            </div>
            {(footer || showBackBtn) && (
                <div
                    className={
                        compactFooter
                            ? "relative shrink-0 flex items-center gap-4 border-t border-border px-6 md:px-8 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
                            : "relative shrink-0 px-6 md:px-8 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-[max(2rem,env(safe-area-inset-bottom))]"
                    }
                >
                    {/* Scroll affordance: fades content into the footer so it's
                        clear there's more below. Only shown when the content
                        actually overflows and isn't scrolled to the bottom. */}
                    <div
                        aria-hidden="true"
                        /* Fades FROM the card's own ground, --surface, at every
                           width. It was --muted below md, which was only ever
                           right because the /me card had no fill on mobile and
                           the page showed through; now that the card is --surface
                           at every width, a --muted fade drew a grey band across
                           a cream card. */
                        className={`pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-[var(--surface)] to-transparent transition-opacity duration-200 ${
                            showFade ? "opacity-100" : "opacity-0"
                        }`}
                    />
                    {compactFooter ? (
                        <>
                            {/* xl, matching the primary the flows put opposite
                                it. Part of the compact treatment rather than a
                                prop: compact IS the wizard footer, and a 48px
                                primary beside a 40px Back is the mismatch this
                                variant exists to avoid. fullWidth keeps lg. */}
                            {showBackBtn && (
                                <Button variant="outline" size="xl" onClick={onBack}>
                                    {backLabel}
                                </Button>
                            )}
                            <div className="ml-auto flex items-center">{footer}</div>
                        </>
                    ) : showBackBtn ? (
                        <div className="flex items-center gap-4">
                            <div className="flex-1">
                                {/* xl (48px), matching the primary beside it. It
                                    was lg (40px), which put an 8px step between
                                    two controls sitting on the same baseline in
                                    the same row — read as a mistake rather than
                                    as hierarchy, which the 1:2 width split is
                                    already carrying. `compact` had reached the
                                    same conclusion for the same reason. */}
                                <Button variant="outline" size="xl" full onClick={onBack}>
                                    {backLabel}
                                </Button>
                            </div>
                            <div className="flex-[2]">{footer}</div>
                        </div>
                    ) : (
                        footer
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="relative flex-1 min-h-0 flex flex-col h-full text-[var(--text-primary)]">
            {/* Progress line in its own row, flush to the top edge of the
                card — unless it is `bare` AND there is a header for it to ride
                on, in which case it is overlaid there instead (below).
                `bare` with no header falls back to this row: the overlay has
                no block to sit on, and silently rendering no progress line at
                all because an unrelated slot is empty is a trap. It still
                draws trackless (`track={!bareProgress}`), so `bare` keeps
                meaning "no groove" either way. */}
            {(!bareProgress || header == null) && progress}

            {/* Persistent chrome (brand/help header) — outside the form so
                Enter-submit semantics stay untouched. */}
            {header != null && (
                <div className={`shrink-0 ${bareProgress ? "relative" : ""}`}>
                    {header}
                    {bareProgress && (
                        <div className="absolute inset-x-0 top-0">{progress}</div>
                    )}
                </div>
            )}

            {onSubmit ? (
                <form onSubmit={onSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {inner}
                </form>
            ) : (
                inner
            )}
        </div>
    );
}
